/**
 * Chart Data Adjuster — enforces readability limits per chart type.
 *
 * Runs AFTER chart type selection. Chart data is shown in full (all categories);
 * we do not group excess categories into "Others". Line/area/scatter may still
 * sample points or trim series for performance; fullData is kept for table view.
 */

// ─── Limits per chart type (used to trim data; type-override by value count is in chartSelectionEngine) ──
const CATEGORY_LIMITS: Record<string, number> = {
  bar: 15,
  horizontal_bar: 12,
  multi_column_bar: 8,
  stacked_bar: 8,
  diverging_bar: 15,
  pie: 6,
  donut: 6,
  sunburst: 6,
  treemap: 25,
  funnel: 7,
  radar: 8,
};

const LINE_SERIES_LIMIT = 5;
const LINE_POINTS_LIMIT = 100;
const AREA_SERIES_LIMIT = 4;
const AREA_POINTS_LIMIT = 80;
const SCATTER_POINTS_LIMIT = 200;
const BUBBLE_POINTS_LIMIT = 50;
const HISTOGRAM_MIN_BINS = 15;
const HISTOGRAM_MAX_BINS = 25;
const HEATMAP_MAX_AXIS = 20;

// ─── Helpers ────────────────────────────────────────────────────────

function isNumericLike(value: unknown): boolean {
  if (value == null || value === '') return false;
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value === 'string') return Number.isFinite(Number(value.trim().replace(/,/g, '')));
  return false;
}

function getPrimaryValue(row: Record<string, any>): number {
  if (isNumericLike(row.value)) return Number(String(row.value).replace(/,/g, '')) || 0;
  const first = Object.keys(row).find(k => k !== 'name' && isNumericLike(row[k]));
  return first != null ? Number(row[first]) || 0 : 0;
}

function sumRow(row: Record<string, any>, exclude: Set<string>): number {
  let total = 0;
  for (const k of Object.keys(row)) {
    if (!exclude.has(k) && isNumericLike(row[k])) total += Number(row[k]) || 0;
  }
  return total;
}

function getNumericKeys(rows: Record<string, any>[]): string[] {
  if (rows.length === 0) return [];
  const keys = Array.from(new Set(rows.flatMap((row) => Object.keys(row))));
  return keys.filter((key) => {
    if (key === 'name') return false;
    const definedValues = rows
      .map((row) => row[key])
      .filter((value) => value !== null && value !== undefined && value !== '');
    return definedValues.length > 0 && definedValues.every((value) => isNumericLike(value));
  });
}

/** Sort descending by primary value (absolute). */
function sortDesc(data: Record<string, any>[]): Record<string, any>[] {
  return [...data].sort((a, b) => Math.abs(getPrimaryValue(b)) - Math.abs(getPrimaryValue(a)));
}

/** Keep top-N, group rest into "Others" row (sums all numeric fields). */
function groupTail(data: Record<string, any>[], keep: number): { adjusted: Record<string, any>[]; grouped: number } {
  if (data.length <= keep) return { adjusted: data, grouped: 0 };

  const sorted = sortDesc(data);
  const top = sorted.slice(0, keep - 1);
  const rest = sorted.slice(keep - 1);

  const numKeys = getNumericKeys(data);
  const others: Record<string, any> = { name: 'Others' };
  for (const k of numKeys) {
    others[k] = rest.reduce((sum, r) => sum + (Number(r[k]) || 0), 0);
  }
  if (numKeys.length === 0) {
    others.value = rest.reduce((sum, r) => sum + getPrimaryValue(r), 0);
  }

  return { adjusted: [...top, others], grouped: rest.length };
}

/** Evenly sample N points from an array. */
function sampleEvenly<T>(arr: T[], n: number): T[] {
  if (arr.length <= n) return arr;
  const step = (arr.length - 1) / (n - 1);
  const result: T[] = [];
  for (let i = 0; i < n; i++) {
    result.push(arr[Math.round(i * step)]);
  }
  return result;
}

/** For multi-series line/area: keep top N series by total absolute value, remove the rest. */
function trimSeries(data: Record<string, any>[], maxSeries: number): { adjusted: Record<string, any>[]; removedSeries: string[] } {
  const numKeys = getNumericKeys(data);
  if (numKeys.length <= maxSeries) return { adjusted: data, removedSeries: [] };

  const totals = numKeys.map(k => ({
    key: k,
    total: data.reduce((s, r) => s + Math.abs(Number(r[k]) || 0), 0),
  }));
  totals.sort((a, b) => b.total - a.total);

  const keepKeys = new Set(totals.slice(0, maxSeries).map(t => t.key));
  const removedSeries = totals.slice(maxSeries).map(t => t.key);

  const adjusted = data.map(row => {
    const newRow: Record<string, any> = {};
    for (const k of Object.keys(row)) {
      if (isNumericLike(row[k]) && k !== 'name' && k !== 'value') {
        if (keepKeys.has(k)) newRow[k] = row[k];
      } else {
        newRow[k] = row[k];
      }
    }
    return newRow;
  });

  return { adjusted, removedSeries };
}

// ─── Main adjuster ──────────────────────────────────────────────────

export interface AdjustmentResult {
  data: any[];
  fullData?: any[];
  dataLimiting?: {
    is_limited: boolean;
    original_count: number;
    displayed_count: number;
  };
  othersCreated: boolean;
  samplingApplied: boolean;
}

export function adjustChartData(
  chartType: string,
  data: any[],
  isMultiSeries?: boolean,
  existingFullData?: any[],
  existingDataLimiting?: { is_limited: boolean; original_count: number; displayed_count: number },
): AdjustmentResult {
  if (!data || data.length === 0) {
    return { data, othersCreated: false, samplingApplied: false };
  }

  const type = chartType?.toLowerCase() || 'bar';
  let result = [...data];
  // Always keep full pre-adjustment list so the table view can show the complete data (no "Others")
  let fullData = existingFullData?.length ? existingFullData : [...data];
  let dataLimiting = existingDataLimiting ?? undefined;
  let othersCreated = false;
  let samplingApplied = false;

  // ── Category-based charts (bar, pie, treemap, funnel, radar) ──
  // Show all data; do not group into "Others" so every category is visible in the chart.
  const catLimit = CATEGORY_LIMITS[type];
  if (catLimit && result.length > catLimit) {
    fullData = fullData ?? [...data];
    const originalCount = dataLimiting?.original_count ?? data.length;
    // Keep full result (no groupTail) so chart displays all categories instead of "Others"
    dataLimiting = {
      is_limited: false,
      original_count: originalCount,
      displayed_count: result.length,
    };
  }

  // ── Line / area: trim series + sample points ──
  if (type === 'line' || type === 'area' || type === 'dual_axis_line') {
    const maxSeries = type === 'area' ? AREA_SERIES_LIMIT : LINE_SERIES_LIMIT;
    const maxPoints = type === 'area' ? AREA_POINTS_LIMIT : LINE_POINTS_LIMIT;

    if (isMultiSeries) {
      const { adjusted, removedSeries } = trimSeries(result, maxSeries);
      if (removedSeries.length > 0) {
        fullData = fullData ?? [...data];
        result = adjusted;
      }
    }

    if (result.length > maxPoints) {
      fullData = fullData ?? [...data];
      const originalCount = dataLimiting?.original_count ?? data.length;
      result = sampleEvenly(result, maxPoints);
      samplingApplied = true;
      dataLimiting = {
        is_limited: true,
        original_count: originalCount,
        displayed_count: result.length,
      };
    }
  }

  // ── Scatter / bubble: sample points ──
  if (type === 'scatter' || type === 'bubble') {
    const limit = type === 'bubble' ? BUBBLE_POINTS_LIMIT : SCATTER_POINTS_LIMIT;
    if (result.length > limit) {
      fullData = fullData ?? [...data];
      const originalCount = dataLimiting?.original_count ?? data.length;
      result = sampleEvenly(result, limit);
      samplingApplied = true;
      dataLimiting = {
        is_limited: true,
        original_count: originalCount,
        displayed_count: result.length,
      };
    }
  }

  // ── Histogram: clamp bin count (show all bins; no "Others" grouping) ──
  if (type === 'histogram') {
    if (result.length > HISTOGRAM_MAX_BINS) {
      fullData = fullData ?? [...data];
      const originalCount = dataLimiting?.original_count ?? data.length;
      // Show all bins; do not group into "Others"
      dataLimiting = {
        is_limited: false,
        original_count: originalCount,
        displayed_count: result.length,
      };
    }
  }

  // ── Heatmap: cap axes to 20×20 ──
  if (type === 'heatmap' || type === 'sensitivity_matrix' || type === 'correlation_matrix') {
    if (result.length > HEATMAP_MAX_AXIS * HEATMAP_MAX_AXIS) {
      fullData = fullData ?? [...data];
      const originalCount = dataLimiting?.original_count ?? data.length;
      result = result.slice(0, HEATMAP_MAX_AXIS * HEATMAP_MAX_AXIS);
      samplingApplied = true;
      dataLimiting = {
        is_limited: true,
        original_count: originalCount,
        displayed_count: result.length,
      };
    }
  }

  return {
    data: result,
    fullData,
    dataLimiting,
    othersCreated,
    samplingApplied,
  };
}
